import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaArrowRight } from 'react-icons/fa';
import { ReactComponent as Logo } from '../assets/logo-light.svg';
import { useAuth } from '../lib/auth';

const NAV_LINKS = [
  { name: 'About', href: '/about' },
  { name: 'Courses', href: '/courses' },
  { name: 'Research', href: '/research' },
  { name: 'Accreditation', href: '/accreditation' },
  { name: 'Programs', href: '/programs' },
  { name: 'Tools', href: '/tools' },
];

// Truncate email for display — show up to 20 chars before the @
function shortEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  const shortLocal = local.length > 16 ? local.slice(0, 14) + '…' : local;
  return `${shortLocal}@${domain}`;
}

// ---------------------------------------------------------------------------
// User menu (desktop)
// ---------------------------------------------------------------------------
function UserMenu({ user, signOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1.5 text-[15px] font-medium text-slate2 hover:text-ink transition-colors"
      >
        <span className="max-w-[160px] truncate">{shortEmail(user.email)}</span>
        <FaChevronDown className={`text-[9px] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-ink/10 shadow-[0_8px_24px_-8px_rgba(15,42,46,0.18)] py-1.5 z-50">
          <div className="px-4 py-2 border-b border-ink/5 mb-1">
            <p className="text-[11px] tracking-[0.14em] uppercase text-brand-aubergine">Signed in as</p>
            <p className="text-sm text-ink truncate mt-0.5">{user.email}</p>
          </div>
          <Link
            to="/my-courses"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ink hover:bg-cream/80 transition-colors"
          >
            My courses
          </Link>
          {user.is_admin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-ink hover:bg-cream/80 transition-colors"
            >
              Admin
            </Link>
          )}
          <div className="border-t border-ink/5 mt-1 pt-1">
            <button
              type="button"
              onClick={() => { setOpen(false); signOut(); }}
              className="block w-full text-left px-4 py-2 text-sm text-slate2 hover:text-ink hover:bg-cream/80 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, loading, signOut } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 bg-bone transition-shadow print:hidden shadow-[0_1px_0_rgba(15,42,46,0.12)] ${
        scrolled ? 'shadow-[0_2px_12px_-4px_rgba(15,42,46,0.10)]' : ''
      }`}
    >
      {/* main bar */}
      <div className="container-page flex items-center justify-between py-2 lg:py-3">
        <Link to="/" className="flex items-center gap-3 group">
          <Logo
            aria-label="Learning Outcomes Academy"
            className="h-12 sm:h-14 w-auto text-[#919091]"
          />
          <span className="sr-only">Learning Outcomes Academy</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.name}
              to={link.href}
              className={({ isActive }) =>
                `relative text-[15px] font-medium tracking-tight transition-colors ${
                  isActive ? 'text-ink' : 'text-slate2 hover:text-ink'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.name}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-brand-deep" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Desktop right side */}
        <div className="hidden lg:flex items-center gap-5">
          {/* Auth zone — invisible during initial load to avoid layout flash */}
          {!loading && (
            user ? (
              <UserMenu user={user} signOut={signOut} />
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-[15px] font-medium text-slate2 hover:text-ink"
                >
                  Sign in
                </Link>
                <Link
                  to="/courses/ai-for-parents"
                  className="inline-flex items-center gap-2 bg-ink text-bone text-[15px] font-medium px-4 py-2 rounded-full hover:bg-brand-deep transition-colors"
                >
                  AI for Parents <FaArrowRight className="text-[10px]" />
                </Link>
              </>
            )
          )}
        </div>

        <button
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-ink/15 text-ink"
        >
          {open ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* mobile drawer */}
      {open && (
        <div id="mobile-nav" className="lg:hidden border-t border-ink/10 bg-bone">
          <div className="container-page py-4 flex flex-col">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.name}
                to={link.href}
                className={({ isActive }) =>
                  `py-3 border-b border-ink/5 text-base font-medium ${
                    isActive ? 'text-ink' : 'text-slate2'
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}

            {!loading && (
              user ? (
                <>
                  <Link
                    to="/my-courses"
                    className="py-3 border-b border-ink/5 text-base font-medium text-ink"
                  >
                    My courses
                  </Link>
                  <button
                    type="button"
                    onClick={signOut}
                    className="mt-3 text-left text-base font-medium text-slate2"
                  >
                    Sign out ({shortEmail(user.email)})
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="mt-3 inline-flex items-center justify-center gap-2 bg-ink text-bone font-medium px-5 py-3 rounded-full"
                  >
                    Sign in
                  </Link>
                </>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
